import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { useUndoable } from "@/hooks/use-undoable";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ShieldWarning, Package, EnvelopeSimple, Plus, X,
  CheckCircle, XCircle, Spinner, Lightning, Wrench, Info,
  ClockClockwise, FileArrowDown, ChatCircleText, ArrowSquareOut, Trash,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { extractErr } from "@/lib/errors";
import AccountMenu from "@/components/AccountMenu";
import PushBell from "@/components/PushBell";
import { useT } from "@/lib/i18n";

const STATUS_FLOW = [
  { key: "submitted", label: "Submitted", color: "bg-slate-100 text-slate-700" },
  { key: "under_review", label: "Under review", color: "bg-amber-100 text-amber-800" },
  { key: "approved", label: "Approved", color: "bg-emerald-100 text-emerald-700" },
  { key: "denied", label: "Denied", color: "bg-rose-100 text-rose-700" },
  { key: "closed", label: "Closed", color: "bg-slate-200 text-slate-600" },
];
const STATUS_LABEL = Object.fromEntries(STATUS_FLOW.map((s) => [s.key, s.label]));
const STATUS_COLOR = Object.fromEntries(STATUS_FLOW.map((s) => [s.key, s.color]));

/**
 * Warranty Claims admin page — list + settings.
 *
 *  ▸ Top:  configurable settings — pick the Employee Position that represents
 *          ACC service department, plus the actual list of recipient e-mails
 *          (legacy DB has no FK between persons & positions, so we keep the
 *          email list explicit).
 *  ▸ Body: list of submitted claims with quick filters and link to detail.
 */
export default function WarrantyClaimsAdminPage({ user, onLogout, onLogoutAll }) {
  const navigate = useNavigate();
  const t = useT();
  const [tab, setTab] = useState("claims"); // 'claims' | 'settings'
  const [claims, setClaims] = useState({ items: [], loading: true });
  const [filterStatus, setFilterStatus] = useState("");

  const [settings, setSettings] = useState(null);
  const [positions, setPositions] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);
  const [serviceEmailDraft, setServiceEmailDraft] = useState("");
  const [ccEmailDraft, setCcEmailDraft] = useState("");
  const [engEmailDraft, setEngEmailDraft] = useState("");

  // Detail drawer
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [statusNote, setStatusNote] = useState("");
  const [statusBusy, setStatusBusy] = useState(false);

  const openDetail = useCallback(async (cid) => {
    setDetailId(cid);
    setDetail(null);
    setStatusNote("");
    try {
      const r = await api.get(`/warranty/claims/${cid}`);
      setDetail(r.data);
    } catch (_) { setDetail({ error: true }); }
  }, []);

  const loadDetail = useCallback(async (cid) => {
    try {
      const r = await api.get(`/warranty/claims/${cid}`);
      setDetail(r.data);
    } catch (_) { /* keep prior detail visible on transient error */ }
  }, []);

  const updateStatus = async (newStatus) => {
    if (!detailId) return;
    setStatusBusy(true);
    try {
      const r = await api.put(`/warranty/claims/${detailId}/status`, {
        status: newStatus, note: statusNote,
      });
      setDetail((d) => ({ ...d, claim: r.data }));
      setStatusNote("");
      // Refresh list to reflect new badge.
      loadClaims();
    } catch (e) {
      toast.error(extractErr(e, "Status update failed").message);
    } finally {
      setStatusBusy(false);
    }
  };

  // Permanently delete the claim — optimistic with 5-second Undo. Backend
  // allows DELETE only when status === 'draft'; we close the drawer + remove
  // the row from the grid immediately, then fire the API after the Undo
  // window expires. If the user clicks Undo, we re-fetch the grid to put
  // the row back (cheaper than tracking the row locally).
  const undoable = useUndoable();
  const requestDiscard = () => {
    if (!detail?.claim) return;
    const c = detail.claim;
    if (c.status !== "draft") {
      toast.error("Only draft claims can be deleted. Submitted claims must be rejected via the status workflow.");
      return;
    }
    const id = detailId;
    const ticketLabel = c.ticket_number || `for ${c.serial_number || c.serial_id}`;
    undoable({
      message: `Discarded draft ${ticketLabel}`,
      description: "Permanent delete in 5 s — click Undo to restore.",
      delayMs: 5000,
      optimisticApply: () => {
        // Remove from the grid + close the drawer instantly.
        setClaims((c) => ({ ...c, items: (c.items || []).filter((r) => r.id !== id) }));
        setDetailId(null);
        setDetail(null);
      },
      perform: () => api.delete(`/warranty/claims/${id}`),
      revert: () => loadClaims(),
      onComplete: () => { /* sonner already shows success message */ },
      onUndo: () => toast.info("Draft restored."),
      testId: "warranty-discard-undo",
    });
  };

  const loadClaims = useCallback(async () => {
    setClaims((c) => ({ ...c, loading: true }));
    try {
      const r = await api.get("/warranty/claims", { params: { status: filterStatus, limit: 200 } });
      setClaims({ items: r.data?.items || [], loading: false });
    } catch (_) {
      setClaims({ items: [], loading: false });
    }
  }, [filterStatus]);

  const loadSettings = useCallback(async () => {
    try {
      const r = await api.get("/warranty/settings");
      setSettings(r.data || {});
    } catch (_) { setSettings({}); }
    try {
      const r2 = await api.get("/positions");
      setPositions(r2.data?.items || []);
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => { loadClaims(); }, [loadClaims]);
  useEffect(() => { if (tab === "settings") loadSettings(); }, [tab, loadSettings]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const r = await api.put("/warranty/settings", {
        service_position_ep_id: settings?.service_position_ep_id || null,
        service_emails: settings?.service_emails || [],
        cc_emails: settings?.cc_emails || [],
        engineering_emails: settings?.engineering_emails || [],
        spike_threshold_x: Number(settings?.spike_threshold_x) || 3.0,
        spike_min_recent: Number(settings?.spike_min_recent) || 3,
        spike_cooldown_hours: Number(settings?.spike_cooldown_hours) || 24,
      });
      setSettings(r.data);
    } catch (e) {
      toast.error(extractErr(e, "Save failed").message);
    } finally {
      setSavingSettings(false);
    }
  };

  const addEmail = (kind) => {
    const draftMap = { svc: serviceEmailDraft, cc: ccEmailDraft, eng: engEmailDraft };
    const fieldMap = { svc: "service_emails", cc: "cc_emails", eng: "engineering_emails" };
    const draft = (draftMap[kind] || "").trim().toLowerCase();
    if (!draft || !draft.includes("@")) return;
    const field = fieldMap[kind];
    const list = (settings?.[field] || []).slice();
    if (list.includes(draft)) return;
    list.push(draft);
    setSettings({ ...settings, [field]: list });
    if (kind === "cc") setCcEmailDraft("");
    else if (kind === "eng") setEngEmailDraft("");
    else setServiceEmailDraft("");
  };

  const removeEmail = (kind, em) => {
    const fieldMap = { svc: "service_emails", cc: "cc_emails", eng: "engineering_emails" };
    const field = fieldMap[kind];
    const list = (settings?.[field] || []).filter((x) => x !== em);
    setSettings({ ...settings, [field]: list });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-[1200px] mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} className="text-slate-500 hover:text-blue-700"
                    data-testid="warranty-admin-back">
              <ArrowLeft size={18} weight="bold" />
            </button>
            <ShieldWarning size={20} weight="fill" className="text-rose-600" />
            <h1 className="text-base font-bold font-['Chivo']">{t("warranty.admin_title", "Warranty Claims · Admin")}</h1>
            <button
              data-testid="warranty-admin-insights"
              onClick={() => navigate("/admin/warranty/insights")}
              className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-violet-100 text-violet-700 hover:bg-violet-200 border border-violet-300 px-2 py-0.5 rounded-sm"
            >
              Insights →
            </button>
          </div>
          <div className="flex items-center gap-2">
            <PushBell />
            <AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} />
          </div>
        </div>
        <div className="max-w-[1200px] mx-auto px-6 flex gap-2 -mb-px">
          {[["claims", "Claims"], ["settings", "Settings"]].map(([k, l]) => (
            <button
              key={k}
              data-testid={`warranty-tab-${k}`}
              onClick={() => setTab(k)}
              className={`text-xs font-bold uppercase tracking-wider px-3 py-2 border-b-2 ${
                tab === k ? "border-rose-600 text-rose-700" : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-6">
        {tab === "claims" && (
          <>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs uppercase font-bold text-slate-500">Status:</span>
              {[["", "All"], ["draft", "Draft"], ["submitted", "Submitted"]].map(([k, l]) => (
                <button
                  key={k || "all"}
                  data-testid={`warranty-filter-${k || "all"}`}
                  onClick={() => setFilterStatus(k)}
                  className={`px-3 py-1 text-xs rounded-sm border ${
                    filterStatus === k ? "bg-rose-600 text-white border-rose-700" : "bg-white border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  {l}
                </button>
              ))}
              <span className="ml-auto text-xs text-slate-500">{claims.items.length} record(s)</span>
            </div>

            <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase font-bold text-slate-500 tracking-wider">
                  <tr>
                    <th className="text-left p-3">{t("wca.col_claim_number", "Claim #")}</th>
                    <th className="text-left p-3">{t("wca.col_compressor", "Compressor")}</th>
                    <th className="text-left p-3">{t("wca.col_customer", "Customer")}</th>
                    <th className="text-left p-3">{t("wca.col_status", "Status")}</th>
                    <th className="text-left p-3">{t("wca.col_emergency", "Emergency")}</th>
                    <th className="text-left p-3">{t("wca.col_submitted", "Submitted")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {claims.loading && (
                    <tr><td colSpan={6} className="p-6 text-center text-xs text-slate-400">
                      <Spinner className="animate-spin inline mr-2" /> Loading…
                    </td></tr>
                  )}
                  {!claims.loading && claims.items.length === 0 && (
                    <tr><td colSpan={6} className="p-6 text-center text-xs text-slate-400">{t("wca.no_claims", "No claims yet.")}</td></tr>
                  )}
                  {claims.items.map((c) => (
                    <tr key={c.id} className="hover:bg-slate-50 cursor-pointer"
                        onClick={() => openDetail(c.id)}
                        data-testid={`warranty-row-${c.id}`}>
                      <td className="p-3 font-mono text-xs font-bold">
                        {c.ticket_number || <span className="text-slate-400">draft</span>}
                      </td>
                      <td className="p-3">
                        <div className="font-bold">{c.product_name || "Compressor"}</div>
                        <div className="text-xs text-slate-500">SN {c.serial_number || c.serial_id}</div>
                      </td>
                      <td className="p-3">{c.company_name || c.customer_id || "—"}</td>
                      <td className="p-3">
                        <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-sm ${STATUS_COLOR[c.status] || "bg-slate-100 text-slate-600"}`}>
                          {STATUS_LABEL[c.status] || c.status || "—"}
                        </span>
                      </td>
                      <td className="p-3">
                        {c.emergency_action === "accepted" && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider bg-rose-100 text-rose-700 px-2 py-0.5 rounded-sm">
                            <Lightning size={10} weight="fill" />Emergency
                          </span>
                        )}
                        {c.emergency_action === "refused" && (
                          <span className="text-xs text-slate-500">No</span>
                        )}
                      </td>
                      <td className="p-3 text-xs text-slate-500">
                        {c.submitted_at ? new Date(c.submitted_at).toLocaleString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "settings" && settings && (
          <div className="max-w-[820px]" data-testid="warranty-settings-pane">
            <h2 className="text-xl font-black font-['Chivo'] mb-4">{t("warranty.admin_settings", "Settings")}</h2>
            <p className="text-sm text-slate-500 mb-6">
              {t("wca.settings_intro_a", "Configure who at ACC receives the auto-emails when a warranty claim is submitted, plus which")}
              <b> {t("wca.employee_position", "Employee Position")}</b>{t("wca.settings_intro_b", " represents the service department.")}
            </p>

            <div className="bg-white border border-slate-200 rounded-sm p-5 mb-4">
              <label className="block text-xs uppercase font-bold tracking-wider text-slate-500 mb-2">
                {t("wca.service_position", "Service-department position")}
              </label>
              <select
                data-testid="warranty-settings-position"
                value={settings.service_position_ep_id || ""}
                onChange={(e) => setSettings({ ...settings, service_position_ep_id: e.target.value || null })}
                className="w-full text-sm border border-slate-300 rounded-sm p-2"
              >
                <option value="">— None —</option>
                {positions.map((p) => (
                  <option key={p.ep_id} value={p.ep_id}>
                    {p.department} · {p.position_name}
                  </option>
                ))}
              </select>
              <div className="text-[11px] text-slate-400 mt-1">
                {t("wca.stored_hint", "Stored alongside the email list. Customers see this label in the e-mail header.")}
              </div>
            </div>

            {/* Service emails */}
            <EmailListBlock
              testid="warranty-settings-service-emails"
              label={t("wca.service_dept_emails", "Service-dept recipient emails")}
              hint={t("wca.service_dept_hint", "These addresses receive the Warranty Claim e-mail (in TO).")}
              draft={serviceEmailDraft}
              setDraft={setServiceEmailDraft}
              list={settings.service_emails || []}
              onAdd={() => addEmail("svc")}
              onRemove={(em) => removeEmail("svc", em)}
            />

            {/* CC emails */}
            <EmailListBlock
              testid="warranty-settings-cc-emails"
              label={t("wca.cc_emails", "CC emails (optional)")}
              hint={t("wca.cc_emails_hint", "Always copied on every submitted claim e-mail.")}
              draft={ccEmailDraft}
              setDraft={setCcEmailDraft}
              list={settings.cc_emails || []}
              onAdd={() => addEmail("cc")}
              onRemove={(em) => removeEmail("cc", em)}
            />

            {/* Engineering recipients (cluster-spike alerts) */}
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-5 mb-4" data-testid="warranty-settings-spike">
              <div className="flex items-center gap-2 mb-3">
                <ChatCircleText size={16} weight="fill" className="text-amber-700" />
                <h4 className="text-sm font-bold font-['Chivo']">{t("wca.cluster_spike_alerts", "Cluster-spike alerts")}</h4>
              </div>
              <p className="text-xs text-amber-800 mb-3">
                {t("wca.cluster_spike_intro", "When a failure-cause cluster (e.g.")} <code>airend_bearing_failure</code>{t("wca.cluster_spike_threshold", ") recurs ≥ threshold × the prior 90-day baseline rate and hits at least")} <b>{t("wca.min_count", "min count")}</b>{t("wca.cluster_spike_cooldown", " claims in 30 days, the engineering recipients below get an automated email + push alert. Cooldown prevents re-alerting within the configured hours.")}
              </p>

              <EmailListBlock
                testid="warranty-settings-engineering-emails"
                label={t("wca.engineering_recipients", "Engineering recipients")}
                hint={t("wca.engineering_hint", "Alerts go to TO on these addresses.")}
                draft={engEmailDraft}
                setDraft={setEngEmailDraft}
                list={settings.engineering_emails || []}
                onAdd={() => addEmail("eng")}
                onRemove={(em) => removeEmail("eng", em)}
              />

              <div className="grid grid-cols-3 gap-3 mt-2">
                <div>
                  <label className="block text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-1">Threshold (×)</label>
                  <input
                    type="number" step="0.1" min="1" max="20"
                    value={settings.spike_threshold_x ?? 3}
                    onChange={(e) => setSettings({ ...settings, spike_threshold_x: e.target.value })}
                    data-testid="warranty-spike-threshold"
                    className="w-full text-sm border border-slate-300 rounded-sm p-2 focus:outline-none focus:border-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-1">{t("wca.min_recent_label", "Min recent (last 30d)")}</label>
                  <input
                    type="number" min="1" max="100"
                    value={settings.spike_min_recent ?? 3}
                    onChange={(e) => setSettings({ ...settings, spike_min_recent: e.target.value })}
                    data-testid="warranty-spike-min-recent"
                    className="w-full text-sm border border-slate-300 rounded-sm p-2 focus:outline-none focus:border-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-1">{t("wca.cooldown_hours_label", "Cooldown (hours)")}</label>
                  <input
                    type="number" min="1" max="720"
                    value={settings.spike_cooldown_hours ?? 24}
                    onChange={(e) => setSettings({ ...settings, spike_cooldown_hours: e.target.value })}
                    data-testid="warranty-spike-cooldown"
                    className="w-full text-sm border border-slate-300 rounded-sm p-2 focus:outline-none focus:border-amber-500"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-6">
              <button
                data-testid="warranty-settings-save"
                disabled={savingSettings}
                onClick={saveSettings}
                className="px-5 py-2 bg-blue-700 text-white text-xs font-bold uppercase rounded-sm hover:bg-blue-800 disabled:opacity-40 inline-flex items-center gap-2"
              >
                {savingSettings ? <Spinner className="animate-spin" size={13} /> : <CheckCircle size={13} weight="bold" />}
                {t("wca.save_settings", "Save settings")}
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Claim detail drawer */}
      {detailId && (
        <div className="fixed inset-0 z-50 flex" onClick={() => { setDetailId(null); setDetail(null); }}>
          <div className="flex-1 bg-black/40 backdrop-blur-sm" />
          <div
            data-testid="warranty-detail-drawer"
            className="w-full max-w-[640px] bg-white border-l border-slate-200 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {!detail && (
              <div className="p-10 text-center text-slate-400">
                <Spinner className="animate-spin inline mr-2" /> Loading…
              </div>
            )}
            {detail?.error && (
              <div className="p-10 text-center text-rose-600">{t("wca.could_not_load", "Could not load claim.")}</div>
            )}
            {detail?.claim && (
              <ClaimDetailDrawer
                detail={detail}
                statusNote={statusNote}
                setStatusNote={setStatusNote}
                onUpdateStatus={updateStatus}
                onDiscard={requestDiscard}
                statusBusy={statusBusy}
                onClose={() => { setDetailId(null); setDetail(null); }}
                onReloadDetail={() => detailId && loadDetail(detailId)}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ClaimDetailDrawer({ detail, statusNote, setStatusNote, onUpdateStatus, onDiscard, statusBusy, onClose, onReloadDetail }) {
  const t = useT();
  const claim = detail.claim;
  const media = detail.media || [];
  const history = claim.status_history || [];
  const ai = claim.ai_analysis || {};
  const parts = claim.parts_snapshot || {};
  const pmQuote = claim.pm_quote || null;
  const linkedQuotes = detail.linked_quotes || [];

  // Labor-quote inline form (auto-stamps the WC-YYYY-NNNNNN ticket on a
  // Q_Type='Warranty' quote per Master Doc v1.4.0 §labor_coverage_*).
  const [showLaborForm, setShowLaborForm] = useState(false);
  const [laborForm, setLaborForm] = useState({ travel_hours: 2, onsite_hours: 2, hourly_rate: 0, note: "" });
  const [laborBusy, setLaborBusy] = useState(false);
  const [sendingQuoteId, setSendingQuoteId] = useState(null);
  const canStampLabor = !!(claim.ticket_number && claim.status && claim.status !== "draft");

  const submitLaborQuote = async () => {
    if (!claim.id) return;
    setLaborBusy(true);
    try {
      const r = await api.post(`/warranty/claims/${claim.id}/labor-quote`, laborForm);
      const msg = (t("wca.labor_quote_success", "Labor quote {quote_id} created with claim {ticket} stamped.") || "")
        .replace("{quote_id}", r.data.quote_id || "?")
        .replace("{ticket}", r.data.ticket_number || claim.ticket_number || "?");
      toast.success(msg);
      setShowLaborForm(false);
      if (onReloadDetail) onReloadDetail();
    } catch (e) {
      const errMsg = e?.response?.data?.detail || e?.message || "unknown error";
      const tpl = t("wca.labor_quote_error", "Could not create labor quote: {err}") || "";
      toast.error(tpl.replace("{err}", errMsg));
    } finally {
      setLaborBusy(false);
    }
  };

  const sendLinkedQuoteToDealer = async (quoteId) => {
    if (!claim.id || !quoteId) return;
    if (!window.confirm(t("wca.send_to_dealer_confirm", "Email this quote to the dealer's primary contacts?"))) return;
    setSendingQuoteId(quoteId);
    try {
      const r = await api.post(`/warranty/claims/${claim.id}/labor-quote/${quoteId}/send-to-dealer`);
      const recipients = (r.data?.recipients || []).join(", ");
      const tpl = t("wca.send_to_dealer_success", "Sent to {recipients}") || "";
      toast.success(tpl.replace("{recipients}", recipients || "—"));
      if (onReloadDetail) onReloadDetail();
    } catch (e) {
      const errMsg = e?.response?.data?.detail || e?.message || "unknown error";
      const tpl = t("wca.send_to_dealer_error", "Could not send: {err}") || "";
      toast.error(tpl.replace("{err}", errMsg));
    } finally {
      setSendingQuoteId(null);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{t("wca.claim_number", "Claim number")}</div>
          <h2 className="text-2xl font-black font-['Chivo'] font-mono">{claim.ticket_number || "—"}</h2>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-sm ${STATUS_COLOR[claim.status] || "bg-slate-100"}`}>
              {STATUS_LABEL[claim.status] || claim.status}
            </span>
            {claim.emergency_action === "accepted" && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold bg-rose-100 text-rose-700 px-2 py-0.5 rounded-sm">
                <Lightning size={10} weight="fill" />Emergency
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {claim.status === "draft" && onDiscard && (
            <button
              onClick={onDiscard}
              disabled={statusBusy}
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-rose-700 hover:text-white border border-rose-200 hover:bg-rose-700 px-2 py-1.5 rounded-sm bg-rose-50/40 disabled:opacity-40"
              data-testid="warranty-detail-discard"
              title={t("wca.discard_tip", "Permanently delete this draft + all attached media")}
            >
              <Trash size={11} weight="fill" /> {t("wca.discard_draft", "Discard draft")}
            </button>
          )}
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="warranty-detail-close">
            <X size={18} weight="bold" />
          </button>
        </div>
      </div>

      <table className="text-sm w-full mb-4">
        <tbody className="[&>tr>th]:text-left [&>tr>th]:text-[11px] [&>tr>th]:uppercase [&>tr>th]:font-bold [&>tr>th]:tracking-wider [&>tr>th]:text-slate-400 [&>tr>th]:py-1 [&>tr>th]:pr-3 [&>tr>td]:py-1">
          <tr><th>{t("table.compressor", "Compressor")}</th><td>{claim.product_name || "—"} <span className="text-slate-500 text-xs">SN {claim.serial_number || claim.serial_id}</span></td></tr>
          <tr><th>{t("table.customer", "Customer")}</th><td>{claim.company_name || claim.customer_id || "—"}</td></tr>
          <tr><th>{t("wca.filed_by", "Filed by")}</th><td className="font-mono text-xs">{claim.created_by || "—"}</td></tr>
          <tr><th>{t("wca.filed_at", "Filed at")}</th><td>{claim.submitted_at ? new Date(claim.submitted_at).toLocaleString() : "—"}</td></tr>
        </tbody>
      </table>

      <Section title={t("wca.findings", "Findings")}>
        <div className="text-sm whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded-sm p-3" data-testid="warranty-detail-findings">
          {claim.findings_text || <span className="text-slate-400">(none)</span>}
        </div>
      </Section>

      {ai?.summary && (
        <Section title={t("wca.ai_analysis_title", "AI analysis (advisory)")}>
          <div className="text-sm">{ai.summary}</div>
          <ul className="text-xs space-y-1 ml-4 list-disc mt-2">
            {(ai.possible_causes || []).slice(0, 5).map((c, i) => (
              <li key={i}><b>[{(c.probability || "").toUpperCase()}]</b> {c.cause} — <i>{c.diagnostic}</i></li>
            ))}
          </ul>
        </Section>
      )}

      {pmQuote?.quote_id && (
        <Section title={t("wca.pm_parts_quote", "Auto-created PM-parts quote")}>
          <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-sm" data-testid="warranty-detail-pm-quote">
            <div><b>{pmQuote.quote_id}</b> · {pmQuote.line_count} line(s) · <b>{pmQuote.currency} {Number(pmQuote.total).toFixed(2)}</b> · status <i>{pmQuote.status}</i></div>
            <a
              href={`/quote/${pmQuote.quote_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-bold uppercase tracking-wider text-blue-700 hover:underline inline-flex items-center gap-1 mt-1"
            >
              {t("wca.open_quote", "Open quote")} <ArrowSquareOut size={11} weight="bold" />
            </a>
          </div>
        </Section>
      )}

      {/* Linked quotes — every quote auto-stamped with this claim's ticket
          number (PM parts auto-draft + labor reimbursement + ad-hoc). */}
      <Section title={t("wca.linked_quotes", "Linked quotes")}>
        <div className="space-y-2 mb-2" data-testid="warranty-detail-linked-quotes">
          {linkedQuotes.length === 0 && (
            <div className="text-xs text-slate-400 italic">—</div>
          )}
          {linkedQuotes.map((lq) => {
            const labelKey = lq.link_type === "labor" ? "wca.linked_quote_labor"
                           : lq.link_type === "pm_parts" ? "wca.linked_quote_pm_parts"
                           : "wca.linked_quote_other";
            const label = t(labelKey, lq.link_type);
            const tone = lq.link_type === "labor"
              ? "bg-violet-50 border-violet-200 text-violet-700"
              : "bg-emerald-50 border-emerald-200 text-emerald-700";
            const sentAt = lq.last_sent_at ? new Date(lq.last_sent_at) : null;
            const sentBusy = sendingQuoteId === lq.quote_id;
            return (
              <div key={lq.quote_id}
                   className="flex items-center gap-3 text-sm bg-white border border-slate-200 rounded-sm p-2 flex-wrap"
                   data-testid={`linked-quote-${lq.quote_id}`}>
                <span className={`inline-flex items-center text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-sm border ${tone}`}>
                  {label}
                </span>
                <span className="font-mono text-xs">{lq.quote_id}</span>
                <span className="text-slate-500 text-xs">
                  {lq.currency} {Number(lq.total || 0).toFixed(2)} · {lq.line_count || 0} {t("wca.line_short", "line(s)")}
                </span>
                {sentAt && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200"
                    title={`${t("wca.sent_at", "Sent at")} ${sentAt.toLocaleString()} · ${(lq.last_sent_to || []).join(", ")}`}
                    data-testid={`linked-quote-sent-${lq.quote_id}`}
                  >
                    <CheckCircle size={10} weight="fill" /> {t("wca.sent", "Sent")}
                  </span>
                )}
                <div className="ml-auto flex items-center gap-2">
                  <button
                    onClick={() => sendLinkedQuoteToDealer(lq.quote_id)}
                    disabled={sentBusy}
                    className="text-xs font-bold uppercase tracking-wider text-violet-700 border border-violet-300 hover:bg-violet-700 hover:text-white px-2 py-1 rounded-sm bg-violet-50/40 inline-flex items-center gap-1 disabled:opacity-40"
                    data-testid={`linked-quote-send-${lq.quote_id}`}
                    title={t("wca.send_to_dealer_hint", "One-click email to dealer's primary contacts.")}
                  >
                    {sentBusy ? (
                      <><Spinner size={11} className="animate-spin" /> {t("wca.sending", "Sending…")}</>
                    ) : (
                      <><EnvelopeSimple size={11} weight="fill" /> {t("wca.send_to_dealer", "Send to dealer")}</>
                    )}
                  </button>
                  <a
                    href={`/quote/${lq.quote_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-bold uppercase tracking-wider text-blue-700 hover:underline inline-flex items-center gap-1"
                    data-testid={`linked-quote-open-${lq.quote_id}`}
                  >
                    {t("wca.open_quote", "Open quote")} <ArrowSquareOut size={11} weight="bold" />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
        {/* Auto-stamp action */}
        {canStampLabor && !showLaborForm && (
          <button
            onClick={() => setShowLaborForm(true)}
            className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-violet-700 border border-violet-300 hover:bg-violet-700 hover:text-white px-3 py-1.5 rounded-sm bg-violet-50/40"
            data-testid="warranty-detail-create-labor-quote"
            title={t("wca.create_labor_quote_hint", "Auto-stamps the claim number on a Q_Type=Warranty quote.")}
          >
            <Wrench size={12} weight="fill" /> {t("wca.create_labor_quote", "Create labor invoice")}
          </button>
        )}
        {showLaborForm && (
          <div className="bg-violet-50 border border-violet-200 rounded-sm p-4 space-y-3" data-testid="labor-quote-form">
            <div className="text-[11px] text-violet-800 font-bold uppercase tracking-wider">
              {t("wca.create_labor_quote", "Create labor invoice")}
            </div>
            <div className="text-xs text-slate-600">{t("wca.create_labor_quote_hint", "Auto-stamps the claim number on a Q_Type=Warranty quote.")}</div>
            <div className="grid grid-cols-3 gap-2">
              <label className="flex flex-col">
                <span className="text-[10px] font-bold uppercase text-slate-500 mb-1">{t("wca.labor_quote_travel", "Travel hours")}</span>
                <input
                  type="number" step="0.25" min="0" max="24"
                  value={laborForm.travel_hours}
                  onChange={(e) => setLaborForm((f) => ({ ...f, travel_hours: parseFloat(e.target.value) || 0 }))}
                  className="text-sm border border-slate-300 rounded-sm p-1.5 focus:outline-none focus:border-violet-500"
                  data-testid="labor-quote-travel"
                />
              </label>
              <label className="flex flex-col">
                <span className="text-[10px] font-bold uppercase text-slate-500 mb-1">{t("wca.labor_quote_onsite", "On-site hours")}</span>
                <input
                  type="number" step="0.25" min="0" max="24"
                  value={laborForm.onsite_hours}
                  onChange={(e) => setLaborForm((f) => ({ ...f, onsite_hours: parseFloat(e.target.value) || 0 }))}
                  className="text-sm border border-slate-300 rounded-sm p-1.5 focus:outline-none focus:border-violet-500"
                  data-testid="labor-quote-onsite"
                />
              </label>
              <label className="flex flex-col">
                <span className="text-[10px] font-bold uppercase text-slate-500 mb-1">{t("wca.labor_quote_rate", "Hourly rate")}</span>
                <input
                  type="number" step="0.01" min="0" max="1000"
                  value={laborForm.hourly_rate}
                  onChange={(e) => setLaborForm((f) => ({ ...f, hourly_rate: parseFloat(e.target.value) || 0 }))}
                  className="text-sm border border-slate-300 rounded-sm p-1.5 focus:outline-none focus:border-violet-500"
                  data-testid="labor-quote-rate"
                />
              </label>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={submitLaborQuote}
                disabled={laborBusy}
                className="px-3 py-1.5 bg-violet-700 text-white text-xs font-bold uppercase rounded-sm hover:bg-violet-800 disabled:opacity-40"
                data-testid="labor-quote-submit"
              >
                {laborBusy ? (
                  <><Spinner size={11} className="inline mr-1 animate-spin" /> {t("wca.labor_quote_creating", "Creating…")}</>
                ) : (
                  t("wca.labor_quote_create", "Create quote")
                )}
              </button>
              <button
                onClick={() => setShowLaborForm(false)}
                disabled={laborBusy}
                className="px-3 py-1.5 border border-slate-300 text-xs font-bold uppercase rounded-sm hover:bg-slate-50 disabled:opacity-40"
                data-testid="labor-quote-cancel"
              >
                {t("wca.labor_quote_cancel", "Cancel")}
              </button>
            </div>
          </div>
        )}
      </Section>

      <Section title={t("wca.parts_purchased_missing", "Parts purchased / missing")}>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div><div className="font-bold mb-1">{t("wca.purchased", "Purchased")} ({parts.purchased_count || 0})</div>
            <ul className="space-y-0.5">
              {(parts.purchased || []).slice(0, 12).map((p, i) => (
                <li key={i} className="text-slate-600 truncate">• {p.product_name} × {p.qty}</li>
              ))}
            </ul>
          </div>
          <div><div className="font-bold mb-1">{t("wca.missing_pm", "Missing PM")} ({parts.missing_count || 0})</div>
            <ul className="space-y-0.5">
              {(parts.missing || []).slice(0, 12).map((m, i) => (
                <li key={i} className="text-rose-700 truncate">• {m.product_name} (qty {m.qty})</li>
              ))}
              {(!parts.missing || parts.missing.length === 0) && <li className="text-slate-400">(none)</li>}
            </ul>
          </div>
        </div>
      </Section>

      {media.length > 0 && (
        <Section title={`Media (${media.length})`}>
          <div className="grid grid-cols-3 gap-2">
            {media.map((m) => (
              <a key={m.id} href={`${process.env.REACT_APP_BACKEND_URL}/api/warranty/media/${m.id}`}
                 target="_blank" rel="noopener noreferrer"
                 className="aspect-square bg-slate-100 border border-slate-200 rounded-sm overflow-hidden block"
                 data-testid={`warranty-detail-media-${m.id}`}>
                {m.mime_type?.startsWith("image/") ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/warranty/media/${m.id}`}
                       alt={m.kind} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-xs text-slate-500">
                    <FileArrowDown size={20} weight="bold" />
                    <span className="mt-1 uppercase">{m.kind.replace("_", " ")}</span>
                  </div>
                )}
              </a>
            ))}
          </div>
        </Section>
      )}

      {/* Status workflow */}
      <Section title={t("wca.status_update", "Status update")}>
        <textarea
          value={statusNote}
          onChange={(e) => setStatusNote(e.target.value)}
          placeholder={t("wca.status_note_placeholder", "Add an internal note (optional, sent in customer notification email)…")}
          rows={3}
          className="w-full text-sm border border-slate-300 rounded-sm p-2 mb-2 focus:outline-none focus:border-blue-500"
          data-testid="warranty-status-note"
        />
        <div className="flex flex-wrap gap-2">
          {STATUS_FLOW.map((s) => (
            <button
              key={s.key}
              data-testid={`warranty-status-${s.key}`}
              disabled={statusBusy || claim.status === s.key}
              onClick={() => onUpdateStatus(s.key)}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-sm border ${
                claim.status === s.key
                  ? `${s.color} border-current cursor-default`
                  : "bg-white border-slate-300 hover:bg-slate-50"
              } disabled:opacity-40`}
            >
              {claim.status === s.key && <CheckCircle size={11} weight="fill" className="inline mr-1" />}
              {s.label}
            </button>
          ))}
        </div>
      </Section>

      {history.length > 0 && (
        <Section title={t("wca.history", "History")}>
          <ol className="space-y-2 text-xs" data-testid="warranty-detail-history">
            {history.slice().reverse().map((h, i) => (
              <li key={i} className="bg-slate-50 border border-slate-100 rounded-sm p-2">
                <div className="flex items-center gap-2">
                  <ClockClockwise size={12} weight="bold" className="text-slate-400" />
                  <span className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded-sm ${STATUS_COLOR[h.status] || "bg-slate-100"}`}>
                    {STATUS_LABEL[h.status] || h.status}
                  </span>
                  <span className="text-slate-500">{t("wca.by", "by")} <span className="font-mono">{h.by}</span></span>
                  <span className="text-slate-400 ml-auto">{new Date(h.at).toLocaleString()}</span>
                </div>
                {h.note && (
                  <div className="mt-1 text-slate-600 flex items-start gap-1.5">
                    <ChatCircleText size={11} weight="bold" className="text-slate-400 mt-0.5 shrink-0" />
                    <span className="whitespace-pre-wrap">{h.note}</span>
                  </div>
                )}
              </li>
            ))}
          </ol>
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-4">
      <h3 className="text-[11px] uppercase font-bold tracking-wider text-slate-500 mb-2">{title}</h3>
      {children}
    </div>
  );
}

function EmailListBlock({ testid, label, hint, draft, setDraft, list, onAdd, onRemove }) {
  const t = useT();
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5 mb-4" data-testid={testid}>
      <label className="block text-xs uppercase font-bold tracking-wider text-slate-500 mb-2">{label}</label>
      <div className="flex gap-2 mb-3">
        <input
          type="email"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(); } }}
          placeholder="name@aircompressorcanada.com"
          className="flex-1 text-sm border border-slate-300 rounded-sm p-2 focus:outline-none focus:border-blue-500"
        />
        <button
          onClick={onAdd}
          className="px-3 py-2 bg-blue-700 text-white text-xs font-bold uppercase rounded-sm hover:bg-blue-800 inline-flex items-center gap-1"
        >
          <Plus size={12} weight="bold" /> {t("wca.add", "Add")}
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {list.length === 0 && <div className="text-xs text-slate-400">{t("wca.no_recipients", "No recipients yet.")}</div>}
        {list.map((em) => (
          <span key={em} className="inline-flex items-center gap-1 bg-slate-100 border border-slate-300 text-xs px-2 py-1 rounded-sm">
            <EnvelopeSimple size={11} weight="bold" className="text-slate-500" />
            {em}
            <button onClick={() => onRemove(em)} className="text-rose-600 hover:text-rose-800 ml-1"><X size={10} weight="bold" /></button>
          </span>
        ))}
      </div>
      <div className="text-[11px] text-slate-400 mt-2">{hint}</div>
    </div>
  );
}
